
import * as React from 'react';

import {
    Avatar,
    Button,
    ContentCard,
    Heading,
    Icon,
    Link,
    Table,
    Text
} from '@/components/ui';
import { DataGrid } from '@/components/business';

import {
    CompactVariantPreview,
    createFamily,
    LabeledPreviewCell,
    LabeledPreviewGrid,
    PreviewRow,
    PreviewStack,
    PreviewSurface,
    simpleTableRows,
    VariantGallery,
    VariantPreview,
    withStorySources
} from './helpers';

const iconCatalogEntriesByFamily = Object.entries({
    sharp: ['bookmark'],
    solid: [
        'alarm',
        'alarms',
        'arrowLeftCircle',
        'actuators',
        'bookmark',
        'buckets',
        'chartColumn',
        'chartFft',
        'chartLine',
        'chartMixed',
        'chartPie',
        'check',
        'circleCheck',
        'circleChevronRight',
        'circleEuro',
        'circleInfo',
        'chevronDown',
        'chevronLeft',
        'chevronRight',
        'chevronsLeft',
        'chevronsRight',
        'circuits',
        'close',
        'contracts',
        'destructive',
        'devices',
        'diagramLeanCanvas',
        'errors',
        'exclamationCircle',
        'eye',
        'eyeSlash',
        'fileExport',
        'globe',
        'info',
        'indicators',
        'linkedIn',
        'magnifyingGlass',
        'markers',
        'notifications',
        'password',
        'plans',
        'preferences',
        'rebuild',
        'rectangleVerticalHistory',
        'reports',
        'sites',
        'success',
        'tariffs',
        'toggleOff',
        'toggleOn',
        'user',
        'users',
        'warning'
    ]
})
    .map(([family, names]) => ({
        family,
        entries: [...names].sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
    }));

const iconSizeEntries = ['small', 'medium', 'large', 'x-large', 'display'];

// Tones mirror Typography.tone for the cross-primitive alignment, with
// `primary` and `secondary` added for icon-led UI. The `background`
// preview sits on a foreground surface so the inverted ink reads.
const iconToneEntries = [
    { value: 'inherit', surfaceClassName: 'tw-bg-background tw-text-foreground' },
    { value: 'foreground', surfaceClassName: 'tw-bg-background' },
    { value: 'muted', surfaceClassName: 'tw-bg-background' },
    { value: 'background', surfaceClassName: 'tw-bg-foreground' },
    { value: 'primary', surfaceClassName: 'tw-bg-background' },
    { value: 'secondary', surfaceClassName: 'tw-bg-background' },
    { value: 'info', surfaceClassName: 'tw-bg-info-surface' },
    { value: 'success', surfaceClassName: 'tw-bg-success-surface' },
    { value: 'warning', surfaceClassName: 'tw-bg-warning-surface' },
    { value: 'destructive', surfaceClassName: 'tw-bg-destructive-surface' }
];

const AvatarStory = () => (
    <VariantPreview className="tw-items-stretch">
        <LabeledPreviewGrid>
            <LabeledPreviewCell label="image">
                <Avatar alt="WM" img="/images/catalog-avatar.jpg" />
            </LabeledPreviewCell>
            <LabeledPreviewCell label="fallback (no image)">
                <Avatar alt="JS" />
            </LabeledPreviewCell>
            <LabeledPreviewCell label="fallback (broken image)">
                <Avatar alt="CT" img="/images/missing.jpg" />
            </LabeledPreviewCell>
        </LabeledPreviewGrid>
    </VariantPreview>
);

const AvatarHero = () => (
    <Avatar alt="WM" img="/images/catalog-avatar.jpg" />
);

const IconStory = () => (
    <PreviewStack>
        <section className="tw-space-y-3">
            <Text tone="muted" variant="overline">
                Sizes
            </Text>
            <VariantPreview className="tw-items-stretch">
                <LabeledPreviewGrid>
                    {iconSizeEntries.map(size => (
                        <LabeledPreviewCell
                            key={size}
                            label={size}
                        >
                            <Icon icon="circleInfo" size={size} />
                        </LabeledPreviewCell>
                    ))}
                </LabeledPreviewGrid>
            </VariantPreview>
        </section>

        <section className="tw-space-y-3">
            <Text tone="muted" variant="overline">
                Tones
            </Text>
            <VariantPreview className="tw-items-stretch">
                <LabeledPreviewGrid>
                    {iconToneEntries.map(entry => (
                        <LabeledPreviewCell
                            key={entry.value}
                            label={entry.value}
                            surfaceClassName={entry.surfaceClassName}
                        >
                            <Icon icon="circleInfo" size="medium" tone={entry.value} />
                        </LabeledPreviewCell>
                    ))}
                </LabeledPreviewGrid>
            </VariantPreview>
        </section>


        <PreviewSurface className="tw-items-stretch">
            <div className="tw-space-y-8">
                {iconCatalogEntriesByFamily.map(section => (
                    <section key={section.family} className="tw-space-y-4">
                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
                            <Text tone="muted" variant="overline">
                                {section.family}
                            </Text>
                            <Text tone="muted" variant="caption">
                                {section.entries.length} icons
                            </Text>
                        </div>
                        <div className="tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-3 lg:tw-grid-cols-4 xl:tw-grid-cols-5">
                            {section.entries.map(name => (
                                <div
                                    key={`${section.family}-${name}`}
                                    className="tw-flex tw-min-h-catalog-preview-sm tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-rounded-xl tw-border tw-border-border tw-bg-background tw-p-4 tw-text-center"
                                >
                                    <Icon family={section.family} icon={name} />
                                    <div className="tw-break-all">
                                        <Heading as="span" variant="title-sm">
                                            {name}
                                        </Heading>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                ))}
            </div>
        </PreviewSurface>
    </PreviewStack>
);

const IconHero = () => (
    <Icon icon="circleInfo" size="medium" tone="info" />
);

const LinkStory = () => {
    const [loading, setLoading] = React.useState(false);

    return (
        <VariantPreview className="tw-items-stretch">
            <LabeledPreviewGrid minColumnWidth="180px">
                <LabeledPreviewCell className="tw-items-start" label="control / external" minHeight="112px">
                    <Link href="https://www.wisemetering.com" icon="globe" text="Visit WiseMetering" variant="control"/>
                </LabeledPreviewCell>
                <LabeledPreviewCell className="tw-items-start" label="control / button" minHeight="112px">
                    <Link
                        icon="arrowLeftCircle"
                        onClick={() => undefined}
                        text="Create free account here"
                        variant="control"
                    />
                </LabeledPreviewCell>
                <LabeledPreviewCell className="tw-items-start" label="control / loading" minHeight="112px">
                    <Link
                        loading={loading}
                        onClick={() => {
                            setLoading(true);
                            window.setTimeout(() => setLoading(false), 900);
                        }}
                        text={loading ? 'Working…' : 'Resend code'}
                        variant="control"
                    />
                </LabeledPreviewCell>
                <LabeledPreviewCell className="tw-items-start" label="body / mailto" minHeight="112px">
                    <Link href="mailto:support@wisemetering.com" text="Contact support" variant="body"/>
                </LabeledPreviewCell>
                <LabeledPreviewCell className="tw-items-start" label="bare / button" minHeight="112px">
                    <Link
                        appearance="bare"
                        onClick={() => undefined}
                        text="Sortable column"
                        variant="overline"
                    />
                </LabeledPreviewCell>
            </LabeledPreviewGrid>
        </VariantPreview>
    );
};

const LinkHero = () => (
    <Link href="https://www.wisemetering.com" icon="globe" text="Visit WiseMetering" variant="control"/>
);

const TableHero = () => {
    const [loading, setLoading] = React.useState(false);

    const handleReload = () => {
        setLoading(true);
        window.setTimeout(() => setLoading(false), 1500);
    };

    const totalRows = simpleTableRows.length;
    const stableCount = simpleTableRows.filter(row => row.status === 'Stable').length;

    return (
        <div className="tw-flex tw-w-full tw-flex-col tw-gap-4">
            <div className="tw-flex tw-justify-end">
                <Button onClick={handleReload} size="small" tone="primary">
                    Simulate reload
                </Button>
            </div>
            <Table
                columns={[
                    { key: 'component', header: 'Component' },
                    { key: 'family', header: 'Family' },
                    { key: 'status', header: 'Status', align: 'end' }
                ]}
                emptyMessage="No components yet"
                footer={`${stableCount} of ${totalRows} stable`}
                loading={loading}
                onRowClick={row => window.alert(`Clicked: ${row.component}`)}
                rowKey="component"
                rows={simpleTableRows}
            />
        </div>
    );
};

const DataGridHero = () => {
    // Domain dictionaries for the indicator-style sample. They live inside the
    // hero (rather than at module scope) so the Code tab shows everything the
    // demo needs in a single self-contained block.
    const KIND_LABEL = {
        electricity: 'Electricity',
        water: 'Water',
        temperature: 'Temperature',
        gas: 'Gas',
        air_quality: 'Air quality'
    };
    const SITES = ['Lisboa HQ', 'Porto Annex', 'Braga Plant', 'Faro Warehouse', 'Coimbra Office', 'Aveiro Facility'];
    const INDICATOR_NAMES = [
        'Active power', 'Reactive power', 'Apparent power', 'Power factor',
        'Cold water consumption', 'Hot water flow', 'Domestic water pressure',
        'Indoor temperature', 'Outdoor temperature', 'HVAC return temp', 'Setpoint',
        'Gas consumption', 'Diesel tank level', 'Boiler pressure', 'Burner status',
        'CO₂ level', 'PM2.5 level', 'Relative humidity', 'Air-quality index',
        'Solar production', 'Wind production', 'Battery state of charge',
        'Lighting load', 'Plug load', 'HVAC load', 'Lift consumption',
        'Network traffic'
    ];

    // Build 75 deterministic rows on first render. `useMemo` keeps the same
    // dataset across re-renders so toggling a filter or paging doesn't reseed
    // the LCG. Enough rows to fill ~8 pages at pageSize=10 so the paginator
    // shows numbered buttons, prev/next and the centred-window ellipsis.
    const dataGridRows = React.useMemo(() => {
        let seed = 1;
        const rand = () => {
            seed = (seed * 9301 + 49297) % 233280;
            return seed / 233280;
        };
        const pick = list => list[Math.floor(rand() * list.length)];
        return Array.from({ length: 75 }, (_, index) => {
            const kind = pick(Object.keys(KIND_LABEL));
            const magnitude = kind === 'temperature' ? 30 : kind === 'air_quality' ? 800 : 12000;
            return {
                id: index + 1,
                name: `${pick(INDICATOR_NAMES)} #${index + 1}`,
                kind,
                site: pick(SITES),
                value: Math.round(rand() * magnitude)
            };
        });
        // The dictionaries above are stable refs (declared in this render's
        // closure with no setters), so we deliberately omit them from deps
        // and let the dataset compute exactly once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Selection is owned by the caller (not persisted to the URL). The grid
    // fires `onSelectionChange(selectedKeys, selectedRows)` whenever the
    // user toggles a Checkbox or the "select all" header box. We only need
    // the keys to drive the trailing "Selected ids" caption — actions get
    // them as their first onClick argument too.
    const [selectedKeys, setSelectedKeys] = React.useState([]);

    return (
        <div className="tw-flex tw-w-full tw-flex-col tw-gap-2">
            <DataGrid
                // Toolbar actions appear above the table. The `requires`
                // constraint gates enablement against selection size:
                //   'none'     → always enabled (page-level action)
                //   'single'   → exactly 1 row picked
                //   'any'      → ≥ 1 rows picked
                //   'multiple' → ≥ 2 rows picked
                // Each `onClick` receives (selectedKeys, selectedRows).
                actions={[
                    { label: 'Refresh', icon: 'refresh', appearance: 'outline', requires: 'none', onClick: () => window.alert('Refresh') },
                    { label: 'Duplicate', icon: 'duplicate', appearance: 'outline', requires: 'single', onClick: keys => window.alert(`Duplicate ${keys[0]}`) },
                    { label: 'Delete', icon: 'delete', appearance: 'outline', tone: 'destructive', requires: 'any', onClick: keys => window.alert(`Delete ${keys.length} item(s)`) }
                ]}
                // Column spec — `key` is the accessor and the URL filter id.
                // `filter` opts the column into a per-column popover; the
                // type drives the widget rendered inside (text/select/
                // numberRange/dateRange). `select` is always multi-capable —
                // the user picks one or many through the same widget.
                // `render(row)` formats the cell while sorting/filtering
                // still use the raw value.
                columns={[
                    {
                        key: 'name',
                        label: 'Name',
                        sortable: true,
                        filter: { type: 'text', placeholder: 'Search name…' }
                    },
                    {
                        key: 'kind',
                        label: 'Kind',
                        sortable: true,
                        // `render` only changes how the cell looks. Sort still
                        // runs on the raw slug ('air_quality'); the select
                        // filter values come from the same slugs.
                        render: row => KIND_LABEL[row.kind] ?? row.kind,
                        filter: {
                            type: 'select',
                            options: Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label }))
                        }
                    },
                    {
                        key: 'site',
                        label: 'Site',
                        sortable: true,
                        filter: {
                            type: 'select',
                            options: SITES.map(value => ({ value, label: value }))
                        }
                    },
                    {
                        key: 'value',
                        label: 'Value',
                        align: 'end',
                        sortable: true,
                        // numberRange exposes two Inputs (from/to). Either
                        // side can be left blank — only the side with a
                        // value constrains the dataset.
                        filter: { type: 'numberRange' },
                        render: row => row.value.toLocaleString()
                    }
                ]}
                // Default sort dialect mirrors the URL: 'name' = ascending
                // by name, '-value' = descending by value. Multi-sort by
                // passing several entries (priority follows array order).
                defaultSort={['name']}
                // Row click is the primary detail action — wiring it adds
                // cursor-pointer + hover paint. Clicks inside the selection
                // cell are skipped so toggling the Checkbox doesn't also
                // fire onRowClick.
                onRowClick={row => window.alert(`Open: ${row.name}`)}
                onSelectionChange={keys => setSelectedKeys(keys)}
                // pageSize accepts three shapes:
                //   number   → fixed N rows per page (this hero)
                //   'auto'   → measure parent on mount, pick what fits
                //   undefined → no paginator, show everything
                pageSize={10}
                // URL persistence is on by default. We turn it off in the
                // catalog so navigating between stories doesn't leak filter
                // / sort / page state into the address bar.
                persistInUrl={false}
                rows={dataGridRows}
                selectable
            />
            {selectedKeys.length > 0 ? (
                <Text tone="muted" variant="caption">
                    Selected ids: {selectedKeys.join(', ')}
                </Text>
            ) : null}
        </div>
    );
};

const displayStorySections = [
    createFamily({
        id: 'avatar',
        label: 'Avatar',
        description: 'Image and fallback avatar primitives.',
        subcomponents: ['Avatar', 'AvatarImage', 'AvatarFallback'],
        stories: [{
            id: 'avatar-states',
            title: 'Image and fallback states',
            render: AvatarStory,
            hero: AvatarHero
        }]
    }),
    createFamily({
        id: 'icon',
        label: 'Icon',
        description: 'Current icon wrapper with the full registry of available icon names.',
        subcomponents: ['Icon'],
        stories: [{
            id: 'icon-registry',
            title: 'Available icons',
            render: IconStory,
            hero: IconHero
        }]
    }),
    createFamily({
        id: 'link',
        label: 'Link',
        description: 'Single link primitive that reuses the shared typography scale for compact and prominent link styles.',
        subcomponents: ['Link'],
        stories: [{
            id: 'link-variants',
            title: 'Typography-aligned link variants',
            render: LinkStory,
            hero: LinkHero
        }]
    }),
    createFamily({
        id: 'table',
        label: 'Table',
        description: 'Closed data-driven table — caller passes `columns` (`{ key, header, align?, render? }`) and `rows`. Primitive owns header chrome, zebra striping, empty state and skeleton loading. Hover/click affordance only when `onRowClick` is wired.',
        subcomponents: ['Table'],
        stories: [{
            id: 'table-basic',
            title: 'Basic table',
            hero: TableHero
        }]
    }),
    createFamily({
        id: 'data-grid',
        label: 'DataGrid',
        layer: 'business',
        description: 'Closed composition for tabular data with multi-column sort, per-column filtering, pagination, selection, and an actions toolbar. Wraps TanStack Table for the data pipeline and composes ui/ primitives (Checkbox, Pagination, Popover, IconButton, Skeleton, Text) for every surface. Caller stays agnostic of TanStack — passes column spec + rows array.',
        subcomponents: ['DataGrid'],
        stories: [{
            id: 'data-grid-basic',
            title: 'Sortable, filterable, selectable grid',
            hero: DataGridHero
        }]
    })
].map(withStorySources);

export { displayStorySections };
